/* eslint-disable max-lines-per-function */
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';
import { useCallback, useState } from 'react';

export type AtOpenSource = 'textarea' | 'button' | null;
interface UseAtProductDropdownProps {
  commandRef: RefObject<HTMLDivElement>;
  focusInput: () => void;
}

export const useAtProductDropdown = ({
  commandRef,
  focusInput,
}: UseAtProductDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<AtOpenSource>(null);

  const onTextareaChangeOpenIfAt = useCallback(
    (value: string, callfirst?: () => void) => {
      if (!/^@\S*$/.test(value)) {
        if (open) {
          setOpen(false);
          setSource(null);
        }
        return;
      }

      if (!open) {
        callfirst?.();
        setOpen(true);
      }

      setSource('textarea');
    },
    [open]
  );

  const onOpenChange = useCallback(
    (status: boolean) => {
      setOpen(status);
      focusInput();
    },
    [focusInput]
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>, onEnterWhenClosed: () => void) => {
      if (event.key === 'Enter' && !event.shiftKey && !open) {
        event.preventDefault();
        onEnterWhenClosed();
        return;
      }

      if (
        open &&
        ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)
      ) {
        event.preventDefault();

        if (event.key === 'Escape') {
          setOpen(false);
          setSource(null);
          return;
        }

        const KeyboardEventConstructor = globalThis.KeyboardEvent;

        if (typeof KeyboardEventConstructor === 'function') {
          commandRef.current?.dispatchEvent(
            new KeyboardEventConstructor('keydown', {
              key: event.key,
              bubbles: true,
            })
          );
        }
      }
    },
    [open, commandRef]
  );

  return {
    open,
    setOpen,
    source,
    setSource,
    onTextareaChangeOpenIfAt,
    onOpenChange,
    onKeyDown,
  };
};
